<script setup lang="ts">
  import { Button } from '@/components/ui/button'
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
  } from '@/components/ui/dialog'
  import { useTranslation } from '@/composables/useTranslation'
  import { useGlobalChatStore } from '@/store/app/globalChat/globalChatStore'
  import type { SessionGroup } from '@/types/generated'
  import { Sparkles } from 'lucide-vue-next'
  import { computed, ref, watch } from 'vue'

  interface Props {
    open: boolean
    folder?: SessionGroup | null
  }

  const props = withDefaults(defineProps<Props>(), {
    folder: null
  })

  const emit = defineEmits<{
    close: []
    saved: [folder: SessionGroup]
  }>()

  const { t } = useTranslation({
    en: {
      createFolder: 'Create New Folder',
      editFolder: 'Edit Folder',
      folderTitle: 'Folder Title',
      folderTitlePlaceholder: 'Folder name',
      folderDescription: 'Description (Optional)',
      folderInstructions: 'Instructions (Optional)',
      folderDescriptionPlaceholder: 'Enter folder description',
      folderInstructionsPlaceholder: 'Enter instructions for all chats in this folder',
      cancel: 'Cancel',
      create: 'Create Folder',
      save: 'Save',
      saving: 'Saving...',
      creating: 'Creating...',
      folderInfo: 'Use folders to group chats, files, and custom instructions.',
      characters: 'characters',
      folderNameExists: 'A folder with this name already exists. Please choose a different name.',
      folderNameExistsError: 'Folder name already exists',
      defaultFolderCannotBeModified:
        'Built-in folders cannot be renamed or deleted. Create a new folder to organize your chats.'
    },
    de: {
      createFolder: 'Neuen Ordner erstellen',
      editFolder: 'Ordner bearbeiten',
      folderTitle: 'Ordnertitel',
      folderTitlePlaceholder: 'Ordnername',
      folderDescription: 'Beschreibung (Optional)',
      folderInstructions: 'Anweisungen (Optional)',
      folderDescriptionPlaceholder: 'Ordnerbeschreibung eingeben',
      folderInstructionsPlaceholder: 'Anweisungen für alle Chats in diesem Ordner eingeben',
      cancel: 'Abbrechen',
      create: 'Ordner erstellen',
      save: 'Speichern',
      saving: 'Speichern...',
      creating: 'Erstellen...',
      folderInfo:
        'Verwenden Sie Ordner, um Chats, Dateien und benutzerdefinierte Anweisungen zu gruppieren.',
      characters: 'Zeichen',
      folderNameExists:
        'Ein Ordner mit diesem Namen existiert bereits. Bitte wählen Sie einen anderen Namen.',
      folderNameExistsError: 'Ordnername bereits vorhanden',
      defaultFolderCannotBeModified:
        'Systemordner können nicht umbenannt oder gelöscht werden. Legen Sie einen neuen Ordner an, um Ihre Chats zu organisieren.'
    }
  })

  const globalChatStore = useGlobalChatStore()

  // Form state
  const form = ref({
    name: '',
    description: '',
    instructions: ''
  })

  const isSubmitting = ref(false)
  const errors = ref<Record<string, string>>({})

  // Computed properties
  const isEditing = computed(() => !!props.folder)
  const title = computed(() => (isEditing.value ? t('editFolder') : t('createFolder')))
  const submitText = computed(() => {
    if (isSubmitting.value) {
      return isEditing.value ? t('saving') : t('creating')
    }
    return isEditing.value ? t('save') : t('create')
  })

  const characterCount = computed(() => form.value.name.length)
  const maxCharacters = 50

  // Watch for folder changes to populate form
  watch(
    () => props.folder,
    (folder) => {
      if (folder) {
        form.value = {
          name: folder.name,
          description: folder.description || '',
          instructions: folder.instructions || ''
        }
      } else {
        form.value = {
          name: '',
          description: '',
          instructions: ''
        }
      }
      errors.value = {}
    },
    { immediate: true }
  )

  // Methods
  const validateForm = () => {
    errors.value = {}

    if (!form.value.name.trim()) {
      errors.value.name = 'Folder title is required'
      return false
    }

    if (form.value.name.length > maxCharacters) {
      errors.value.name = `Folder title must be less than ${maxCharacters} characters`
      return false
    }

    // Check for duplicate folder names (only when creating new folders)
    if (!isEditing.value) {
      const existingFolder = globalChatStore.sessionGroups.find(
        (folder) => folder.name.toLowerCase() === form.value.name.trim().toLowerCase()
      )
      if (existingFolder) {
        errors.value.name = t('folderNameExists')
        return false
      }
    }

    return true
  }

  const handleSubmit = async () => {
    if (!validateForm()) return
    if (isEditing.value && props.folder?.is_default) {
      errors.value = { general: t('defaultFolderCannotBeModified') }
      return
    }

    try {
      isSubmitting.value = true
      errors.value = {}
      globalChatStore.clearError()

      if (isEditing.value && props.folder) {
        // Update existing folder
        const updatedFolder = await globalChatStore.updateSessionGroup(props.folder.uuid, {
          name: form.value.name,
          description: form.value.description || undefined,
          instructions: form.value.instructions || undefined
        })
        emit('saved', updatedFolder)
      } else {
        // Create new folder
        const newFolder = await globalChatStore.createSessionGroup({
          name: form.value.name,
          description: form.value.description || undefined,
          instructions: form.value.instructions || undefined
        })
        emit('saved', newFolder)
      }
    } catch (error: any) {
      console.error('Error saving folder:', error)

      // Handle specific error cases
      if (error?.response?.status === 403) {
        errors.value.general = t('defaultFolderCannotBeModified')
      } else if (error?.response?.status === 400 && error?.response?.data?.name) {
        // Backend validation error for duplicate name
        errors.value.name = t('folderNameExists')
      } else {
        // Generic error fallback
        errors.value.general = 'Failed to save folder. Please try again.'
      }
    } finally {
      isSubmitting.value = false
    }
  }

  const handleClose = () => {
    if (!isSubmitting.value) {
      emit('close')
    }
  }
</script>

<template>
  <Dialog
    :open="open"
    @update:open="(value) => !value && handleClose()"
  >
    <DialogContent class="w-[609px] p-6">
      <DialogHeader>
        <DialogTitle class="text-xl font-semibold text-gray-800">
          {{ title }}
        </DialogTitle>
        <DialogDescription>
          {{ t('folderInfo') }}
        </DialogDescription>
      </DialogHeader>

      <!-- Informational Banner -->
      <div class="flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 p-2">
        <Sparkles class="h-4 w-4 text-blue-700" />
        <p class="text-xs font-medium text-blue-700">
          {{ t('folderInfo') }}
        </p>
      </div>

      <!-- Folder Title Input -->
      <div class="space-y-1">
        <label class="text-sm font-medium text-gray-600">
          {{ t('folderTitle') }}
        </label>
        <div class="relative">
          <input
            v-model="form.name"
            type="text"
            class="h-10 w-full rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-base shadow-sm
              placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            :placeholder="t('folderTitlePlaceholder')"
            :maxlength="maxCharacters"
            data-cy="folder-name-input"
          />
        </div>
        <div class="flex items-center justify-between">
          <div
            v-if="errors.name"
            class="text-sm text-red-600"
          >
            {{ errors.name }}
          </div>
          <div class="ml-auto text-xs text-gray-500">
            {{ characterCount }}/{{ maxCharacters }} {{ t('characters') }}
          </div>
        </div>
      </div>

      <!-- General Error -->
      <div
        v-if="errors.general"
        class="text-sm text-red-600"
      >
        {{ errors.general }}
      </div>

      <DialogFooter class="gap-2">
        <Button
          variant="secondary"
          :disabled="isSubmitting"
          @click="handleClose"
        >
          {{ t('cancel') }}
        </Button>
        <Button
          :disabled="isSubmitting || !form.name.trim()"
          class="bg-purple-600 hover:bg-purple-700"
          data-cy="save-folder-button"
          @click="handleSubmit"
        >
          {{ submitText }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
